import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Check, ArrowLeft, Image as ImageIcon, LogOut } from 'lucide-react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { clearToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImage = (img) => {
  if (!img) return img;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  return img;
};

export default function Admin() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const fileInputRefs = useRef({});

  const handleLogout = () => {
    clearToken();
    navigate('/login', { replace: true });
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleFileChange = async (productId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    setSaving(productId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/products/${productId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSavedId(productId);
      await fetchProducts();
      setTimeout(() => setSavedId(null), 2000);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erro ao enviar imagem. Tente novamente.');
    } finally {
      setSaving(null);
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] py-12 px-6" data-testid="admin-page">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-12 gap-4">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-[#7dd3fc] transition-colors mb-4" data-testid="admin-back-link">
              <ArrowLeft size={18} />
              <span>Voltar ao site</span>
            </Link>
            <h1 className="text-4xl font-bold text-white">
              Painel <span className="text-[#7dd3fc]">Admin</span>
            </h1>
            <p className="text-white/50 mt-2">Gerencie as imagens dos produtos</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 rounded-lg transition-all duration-300"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} />
            <span>Sair</span>
          </button>
        </div>

        {/* Products List */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
              data-testid={`admin-product-${product.id}`}
            >
              {/* Image Preview */}
              <div className="relative h-56 overflow-hidden bg-[#0f1f3a]">
                <img
                  src={resolveImage(product.image)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 px-3 py-1 bg-white/10 backdrop-blur-xl rounded-full border border-white/20">
                  <span className="text-white text-xs font-bold">{product.weight}</span>
                </div>
                {!product.available && (
                  <div className="absolute top-4 left-4 px-3 py-1 bg-[#0a1628] border border-[#7dd3fc] rounded-full">
                    <span className="text-[#7dd3fc] text-xs font-bold">EM BREVE</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-1">{product.name}</h3>
                <p className="text-white/40 text-sm mb-4">{product.weight}</p>

                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => (fileInputRefs.current[product.id] = el)}
                  onChange={(e) => handleFileChange(product.id, e)}
                  className="hidden"
                  data-testid={`admin-file-input-${product.id}`}
                />

                <button
                  onClick={() => fileInputRefs.current[product.id]?.click()}
                  disabled={saving === product.id}
                  className="w-full py-3 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold rounded-xl hover:shadow-lg hover:shadow-[#7dd3fc]/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid={`admin-upload-btn-${product.id}`}
                >
                  {saving === product.id ? (
                    <span>Enviando...</span>
                  ) : savedId === product.id ? (
                    <>
                      <Check size={18} />
                      <span>Salvo!</span>
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      <span>Alterar Imagem</span>
                    </>
                  )}
                </button>

                <p className="text-white/30 text-xs mt-3 text-center flex items-center justify-center gap-1">
                  <ImageIcon size={12} />
                  JPG, PNG ou WEBP (máx. 5MB)
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
